import { useLayoutEffect, useRef, useState } from 'react';

const FALLBACK_MAX_LINES = 5;

/** Font size tiers in cqw units, ordered largest to smallest. */
const SIZE_TIERS = [
  { fontSize: 12.12, lineHeight: 11.5, maxLines: 1 },
  { fontSize: 9.09, lineHeight: 8.6, maxLines: 2 },
  { fontSize: 7.58, lineHeight: 7.2, maxLines: 3 },
  { fontSize: 6.36, lineHeight: 6.0, maxLines: FALLBACK_MAX_LINES },
] as const;

/** Max width of the name area as a percentage of container width. */
const MAX_WIDTH_CQW = 47.64;

interface DynamicNameSizeResult {
  /** Ref to attach to the container element (must be a CSS `@container`) */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** The calculated font size in cqw units */
  fontSize: number;
  /** The calculated line height in cqw units */
  lineHeight: number;
  /** Maximum lines to display (for use with `line-clamp`) */
  maxLines: number;
  /** Whether measurement is complete and values are ready to use */
  isReady: boolean;
}

/**
 * Dynamically determines the optimal font size for a name on a certificate.
 *
 * Works by creating a hidden measurement element, then testing font sizes from
 * largest to smallest until finding one where the name fits within the allowed
 * line count. Uses cqw (container query width) units for responsive scaling.
 */
export const useDynamicNameSize = (name: string): DynamicNameSizeResult => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [sizeIndex, setSizeIndex] = useState(0);
  const [isReady, setIsReady] = useState(!name);

  const [prevName, setPrevName] = useState(name);
  if (prevName !== name) {
    setPrevName(name);
    if (!name) {
      setIsReady(true);
      setSizeIndex(0);
    } else {
      setIsReady(false);
    }
  }

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !name) {
      return;
    }

    document.fonts.ready.then(() => {
      const containerWidth = container.offsetWidth;
      if (containerWidth === 0) {
        setIsReady(true);
        return;
      }

      // Create hidden element for measuring text dimensions
      let measureEl = measureRef.current;
      if (!measureEl) {
        measureEl = document.createElement('div');
        measureEl.style.position = 'absolute';
        measureEl.style.visibility = 'hidden';
        measureEl.style.pointerEvents = 'none';
        measureEl.style.fontFamily = getComputedStyle(container).fontFamily;
        measureEl.style.fontWeight = '500';
        measureEl.style.letterSpacing = '-0.05em';
        measureEl.style.overflowWrap = 'break-word';
        measureEl.style.whiteSpace = 'normal';
        document.body.appendChild(measureEl);
        measureRef.current = measureEl;
      }

      const maxWidthPx = (MAX_WIDTH_CQW / 100) * containerWidth;
      measureEl.style.maxWidth = `${maxWidthPx}px`;
      measureEl.textContent = name;

      // Default to smallest tier (fallback)
      let selectedIndex = SIZE_TIERS.length - 1;

      // Test each tier (largest first) to find the biggest size that fits
      for (let i = 0; i < SIZE_TIERS.length - 1; i++) {
        const tier = SIZE_TIERS[i];
        if (!tier) {
          continue;
        }
        const fontSizePx = (tier.fontSize / 100) * containerWidth;
        const lineHeightPx = (tier.lineHeight / 100) * containerWidth;

        measureEl.style.fontSize = `${fontSizePx}px`;
        measureEl.style.lineHeight = `${lineHeightPx}px`;

        // Measure single-line height using chars with full vertical extent
        measureEl.textContent = 'Xg';
        const singleLineHeight = measureEl.scrollHeight;

        // Measure actual name height and calculate line count
        measureEl.textContent = name;
        const actualHeight = measureEl.scrollHeight;
        const rawRatio = actualHeight / singleLineHeight;
        const lineCount = Math.ceil(rawRatio - 0.4); // Tolerance for font metrics variance

        if (lineCount <= tier.maxLines) {
          selectedIndex = i;
          break;
        }
      }

      setSizeIndex(selectedIndex);
      setIsReady(true);
    });

    return () => {
      if (measureRef.current) {
        document.body.removeChild(measureRef.current);
        measureRef.current = null;
      }
    };
  }, [name]);

  const defaultTier = SIZE_TIERS.at(0);
  if (!defaultTier) {
    throw new Error('SIZE_TIERS must include at least one tier');
  }

  const tier = SIZE_TIERS[sizeIndex] ?? defaultTier;

  return {
    containerRef,
    fontSize: tier.fontSize,
    lineHeight: tier.lineHeight,
    maxLines: tier.maxLines,
    isReady,
  };
};
